<template>
  <div
    date-component="BaseInput"
    class="base-input"
  >
    <div class="header">
      <label
        v-if="label"
        :for="id"
        :id="id + '-label'"
        v-html="label"
      ></label>

      <BaseTooltip
        v-if="tooltip"
        :text="tooltip"
      />
    </div>

    <v-text-field
      :id="id"
      ref="$field"
      :aria-describedby="fieldValidationErrorMessage ? id + '-error' : undefined"
      :aria-invalid="!!fieldValidationErrorMessage"
      :aria-required="rules.length > 0"
      :placeholder="placeholder"
      :modelValue="value"
      @update:modelValue="value = !!uppercase ? $event.trim().toUpperCase() : $event.trim()"
      :rules="rules"
      :hide-details="true"
      :readonly="!editable"
      :disabled="!editable"
      :loading="loading"
      :autocomplete="autocomplete"
      :error="serviceValidationError"
      :error-messages="serviceValidationMessage"
      :maxlength="maxlength"
      :inputmode="inputmode"
      :type="inputType"
      @paste="emit('paste', $event)"
      @update:focused="emit('focus', $event)"
    >
      <template
        v-if="icon"
        v-slot:prepend-inner
      >
        <v-icon
          class="prepend-inner-icon"
          :icon="icon"
          aria-hidden="true"
        />
        <span :class="{ error: !!fieldValidationErrorMessage }">
          {{ iconText }}
        </span>
      </template>
    </v-text-field>

    <ErrorMessageComponent
      :messages="fieldValidationErrorMessage"
      :id="id + '-error'"
    />
  </div>
</template>

<script setup lang="ts">
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import type { AsyncValidationRuleType, ValidationRuleType } from '@/modules/core/models/types'
  import { nextTick, onBeforeUnmount, onMounted } from 'vue'
  import { BaseTooltip } from '.'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '../../composables/accesibilityComposable'
  import InputmodeTypes from '../../models/InputmodeTypes'
  import InputTypes from '../../models/InputTypes'
  import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'

  interface PropsInterface {
    id: string
    modelValue: string | null
    placeholder?: string
    rules?: Array<ValidationRuleType | AsyncValidationRuleType>
    label?: string
    icon?: string
    iconText?: string
    loading?: boolean
    serviceValidationError?: boolean
    serviceValidationMessage?: string
    editable?: boolean
    maxlength?: number
    autocomplete?: string
    uppercase?: boolean
    inputmode?: InputmodeTypes
    inputType?: InputTypes
    tooltip?: string
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    rules: () => [],
    loading: false,
    serviceValidationError: false,
    serviceValidationMessage: '',
    editable: true,
    inputmode: InputmodeTypes.Text,
    inputType: InputTypes.Text
  })

  const emit = defineEmits(['paste', 'focus'])

  const value = defineModel()

  const { $field, fieldValidationErrorMessage } = useValidationError()

  let observer: MutationObserver | null = null

  onMounted(async () => {
    // Esperar render inicial para eliminar labels vuetify duplicadas (accesibilidad)
    await nextTick()
    const rootElement = document.getElementById(props.id)?.parentElement as HTMLElement | null
    observer = startDuplicatedLabelControl(rootElement)
  })

  onBeforeUnmount(() => {
    endDuplicatedLabelControl(observer)
  })
</script>

<style lang="scss" scoped>
  .base-input {
    display: flex;
    flex-direction: column;

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: $spacing-s;

      label {
        margin-right: $spacing-l;
        font-family: $font-family-primary;
        font-size: $font-size-m;
        font-style: normal;
        font-weight: $font-weight-400;
        line-height: $line-height-100p;
        color: $color-neutral-dark;
        margin-bottom: $spacing-s;
      }
    }

    :deep(.v-input) {
      &:focus-within {
        box-shadow: 0 0 0 4px $color-focus;
      }
    }

    :deep(.v-field) {
      background-color: $color-grey-200;
      border-radius: $b-radius-0;
      box-shadow: $box-shadow-0;
      transition: all 0.2s ease-in-out;

      input {
        &:focus,
        &:focus-visible {
          outline: none;
          box-shadow: none;
        }
      }

      .v-field__prepend-inner {
        span {
          margin-left: $spacing-xs;
          padding: 0 $spacing-xs;

          &.error {
            color: $color-error;
          }
        }

        i::before {
          font-family: $font-family-icons;
        }
      }

      .v-field__input {
        font-family: $font-family-primary;
        color: $color-neutral-dark;

        &::placeholder {
          color: $color-neutral;
          opacity: 1;
        }
      }

      &.v-field--disabled {
        opacity: 1;
      }

      &.v-field--error {
        background-color: $color-error-200;

        .v-field__input {
          color: $color-error;

          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }
      }
    }
  }
</style>
